import { createFileRoute, ClientOnly } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAccount, useChainId, useSwitchChain } from "wagmi";
import { openAppKit } from "@/lib/wallet";
import { signedCreateDrop, signedAddDropItems } from "@/lib/signed-actions";
import { uploadDropImage, ipfsToHttp } from "@/lib/image-upload";
import { networks } from "@/lib/wallet-networks";
import { DEFAULT_CHAIN_ID } from "@/config/robotdex";
import { executeFeeSplit } from "@/lib/fee-split";
import { assertBalance, getExplorerUrl } from "@/lib/chain-utils";
import { supabase } from "@/integrations/supabase/client";
import { isAddress } from "viem";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { z } from "zod";
import { ConnectButton } from "@/components/ConnectButton";
import { Upload, X, ImagePlus } from "lucide-react";

const createSchema = z.object({
  name: z.string().trim().min(2, "Name is too short").max(80, "Name too long"),
  symbol: z.string().trim().min(2, "Symbol too short").max(10, "Max 10 characters").regex(/^[A-Za-z0-9]+$/, "Letters/numbers only"),
  description: z.string().trim().max(1000, "Max 1000 characters").optional(),
  logo_url: z.string().trim().url("Must be a valid URL").optional().or(z.literal("")),
  mint_price: z.number().min(0, "Must be ≥ 0").max(1_000_000, "Too large"),
  max_supply: z.number().int("Whole number").min(1, "At least 1").max(10_000_000, "Too large").optional(),
  start_at: z.string().optional(),
  end_at: z.string().optional(),
}).refine(
  (v) => !v.start_at || !v.end_at || new Date(v.start_at) < new Date(v.end_at),
  { path: ["end_at"], message: "End must be after start" },
);

export const Route = createFileRoute("/create")({
  component: () => (<ClientOnly fallback={null}><Create /></ClientOnly>),
  head: () => ({ meta: [{ title: "Create - RobotDex" }, { name: "description", content: "Launch your NFT drop on RobotDex. Choose supply, price, and mint window." }] }),
});

function Create() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const defaultChain = networks.find((n) => n.id === DEFAULT_CHAIN_ID) ?? networks[0];
  const [selectedChainId, setSelectedChainId] = useState<number>(defaultChain?.id ?? DEFAULT_CHAIN_ID);
  const selectedNetwork = networks.find((n) => n.id === selectedChainId) ?? networks[0];
  const currencySymbol = selectedNetwork?.nativeCurrency.symbol ?? "ETH";

  const [busy, setBusy] = useState(false);
  const [networkCfg, setNetworkCfg] = useState<Record<number, { fee_recipient: string | null; create_fee_wei: string; enabled: boolean }>>({});
  useEffect(() => {
    let cancelled = false;
    supabase.from("platform_network_settings").select("*").then(({ data }) => {
      if (cancelled || !data) return;
      const map: typeof networkCfg = {};
      for (const r of data as any[]) map[r.chain_id] = { fee_recipient: r.fee_recipient, create_fee_wei: r.create_fee_wei ?? "0", enabled: r.enabled ?? true };
      setNetworkCfg(map);
    });
    return () => { cancelled = true; };
  }, []);
  const activeCfg = networkCfg[selectedChainId];
  const createFeeWei = BigInt(activeCfg?.create_fee_wei || "0");
  const createFeeEth = createFeeWei > 0n
    ? (Number(createFeeWei) / 1e18).toLocaleString(undefined, { maximumFractionDigits: 8 })
    : "0";



  const [form, setForm] = useState({
    name: "", symbol: "", description: "", logo_url: "",
    mint_price: "0", unlimited: true, max_supply: "1000",
    start_at: "", end_at: "",
  });
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const onSelectChain = (idStr: string) => {
    const id = Number(idStr);
    setSelectedChainId(id);
    if (isConnected && chainId !== id) {
      try { switchChain({ chainId: id }); } catch { /* ignore */ }
    }
  };


  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [logoUploading, setLogoUploading] = useState(false);
  const [gallery, setGallery] = useState<{ url: string; preview: string; name: string; size: number }[]>([]);
  const [galleryUploading, setGalleryUploading] = useState(false);

  async function handleLogoFile(file: File) {
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    if (!isConnected || !address) { openAppKit(); return; }
    setLogoUploading(true);
    try {
      const { url, size } = await uploadDropImage(file, "logos", address);
      set("logo_url", url);
      toast.success(`Logo uploaded (${Math.round(size / 1024)} KB)`);
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally { setLogoUploading(false); }
  }

  async function handleGalleryFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (!isConnected || !address) { openAppKit(); return; }
    const remaining = 100 - gallery.length;
    if (remaining <= 0) return toast.error("Gallery is full (100 max)");
    const list = Array.from(files).slice(0, remaining);
    setGalleryUploading(true);
    try {
      for (const f of list) {
        if (!f.type.startsWith("image/")) continue;
        const { url, gateway_url, size } = await uploadDropImage(f, "gallery", address);
        setGallery((g) => [...g, { url, preview: gateway_url, name: f.name.replace(/\.[^.]+$/, ""), size }]);
      }
      toast.success(`Uploaded ${list.length} image${list.length > 1 ? "s" : ""}`);
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally { setGalleryUploading(false); }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConnected || !address) { openAppKit(); return; }
    const parsed = createSchema.safeParse({
      name: form.name,
      symbol: form.symbol,
      description: form.description,
      logo_url: form.logo_url,
      mint_price: Number(form.mint_price || "0"),
      max_supply: form.unlimited ? undefined : Number(form.max_supply || "0"),
      start_at: form.start_at,
      end_at: form.end_at,
    });
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      parsed.error.issues.forEach((i) => { errs[String(i.path[0])] = i.message; });
      setFieldErrors(errs);
      toast.error("Please fix the highlighted fields");
      return;
    }
    setFieldErrors({});
    const v = parsed.data;
    setBusy(true);
    try {
      // Ensure wallet is on the selected network before sending fee tx.
      if (chainId !== selectedChainId) {
        try { await switchChain({ chainId: selectedChainId }); } catch { /* user may reject */ }
      }

      // Look up per-network settings (with global fallback recipient).
      const [{ data: netRow }, { data: globalRow }] = await Promise.all([
        supabase.from("platform_network_settings").select("*").eq("chain_id", selectedChainId).maybeSingle(),
        supabase.from("platform_settings").select("fee_recipient").eq("id", 1).maybeSingle(),
      ]);
      if (netRow && (netRow as any).enabled === false) {
        throw new Error(`${selectedNetwork?.name} is disabled by the admin.`);
      }
      const netFeeWei = BigInt(((netRow as any)?.create_fee_wei as string) || "0");
      const feeRecipient = ((netRow as any)?.fee_recipient as string | null) ?? ((globalRow as any)?.fee_recipient as string | null);
      if (netFeeWei > 0n && (!feeRecipient || !isAddress(feeRecipient))) {
        throw new Error(`No platform wallet configured for ${selectedNetwork?.name}. Ask the admin to set it.`);
      }

      let createFeeTx: string | null = null;
      if (netFeeWei > 0n && feeRecipient && isAddress(feeRecipient)) {
        await assertBalance(address as `0x${string}`, selectedChainId, netFeeWei);
        toast.message("Confirm the Create Drop fee in your wallet…");
        const { mainHash } = await executeFeeSplit([
          { label: "Create Drop fee", to: feeRecipient as `0x${string}`, value: netFeeWei },
        ]);
        createFeeTx = mainHash;
        const link = getExplorerUrl(selectedChainId, mainHash);
        toast.success("Create fee sent", {
          description: link ? mainHash.slice(0, 10) + "…" : undefined,
          action: link ? { label: "View", onClick: () => window.open(link, "_blank") } : undefined,
        });
      }

      const priceWei = v.mint_price > 0
        ? BigInt(Math.floor(v.mint_price * 1e18)).toString()
        : "0";
      const { id: dropId } = await signedCreateDrop(address, {
        name: v.name,
        symbol: v.symbol.toUpperCase(),
        description: v.description || null,
        logo_url: v.logo_url || null,
        chain_id: selectedChainId,
        mint_price_wei: priceWei,
        max_supply: form.unlimited ? null : v.max_supply ?? null,
        start_at: v.start_at ? new Date(v.start_at).toISOString() : null,
        end_at: v.end_at ? new Date(v.end_at).toISOString() : null,
      });
      if (gallery.length > 0) {
        toast.message("Adding collection items…");
        await signedAddDropItems(address, dropId, gallery.map((g, i) => ({
          name: g.name || `#${i + 1}`,
          image_url: g.url,
          attributes: null,
        })));
      }
      toast.success(`Drop created${gallery.length ? ` with ${gallery.length} items` : ""}!${createFeeTx ? " Fee sent on-chain." : ""}`);
      setForm({ name: "", symbol: "", description: "", logo_url: "", mint_price: "0", unlimited: true, max_supply: "1000", start_at: "", end_at: "" });
      setGallery([]);
    } catch (err: any) {
      toast.error(err.shortMessage ?? err.message ?? "Failed to create drop");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
      <PageHeader title="Create NFT Drop" subtitle="Launch a collection anyone can mint" />
      <Card className="p-6 bg-card/60 neon-border">
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" error={fieldErrors.name}>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Robot Genesis" aria-invalid={!!fieldErrors.name} />
            </Field>
            <Field label="Symbol" error={fieldErrors.symbol}>
              <Input value={form.symbol} onChange={(e) => set("symbol", e.target.value)} placeholder="RBOT" maxLength={10} aria-invalid={!!fieldErrors.symbol} />
            </Field>
          </div>
          <Field label="Collection logo" error={fieldErrors.logo_url}>
            <div className="flex items-center gap-3 flex-wrap">
              {form.logo_url && (
                <img src={ipfsToHttp(form.logo_url)} alt="Logo preview" className="h-16 w-16 rounded-md object-cover neon-border" />
              )}
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-border/70 bg-background/40 px-3 py-2 text-sm hover:bg-background/70">
                <Upload className="h-4 w-4" />
                {logoUploading ? "Uploading…" : form.logo_url ? "Replace image" : "Upload logo"}
                <input type="file" accept="image/*" className="hidden" disabled={logoUploading}
                  onChange={(e) => e.target.files?.[0] && handleLogoFile(e.target.files[0])} />
              </label>
              {form.logo_url && (
                <Button type="button" variant="ghost" size="sm" onClick={() => set("logo_url", "")}>Remove</Button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Auto-compressed to WebP (~200 KB max, up to 1600px).</p>
          </Field>
          <Field label="Short description" error={fieldErrors.description}>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} maxLength={1000} placeholder="Tell collectors what makes this drop special." />
          </Field>

          <Field label={`Collection gallery - ${gallery.length}/100 items`}>
            <div className="rounded-md border border-dashed border-border/70 p-3 bg-background/30">
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-border/70 bg-background/40 px-3 py-2 text-sm hover:bg-background/70">
                <ImagePlus className="h-4 w-4" />
                {galleryUploading ? "Uploading…" : "Add images"}
                <input type="file" accept="image/*" multiple className="hidden" disabled={galleryUploading || gallery.length >= 100}
                  onChange={(e) => { handleGalleryFiles(e.target.files); e.currentTarget.value = ""; }} />
              </label>
              <p className="text-[11px] text-muted-foreground mt-2">
                Up to 100 items. Each minter gets a random unassigned item as their NFT metadata.
              </p>
              {gallery.length > 0 && (
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {gallery.map((g, i) => (
                    <div key={i} className="relative group aspect-square">
                      <img src={g.preview} alt={g.name} className="w-full h-full object-cover rounded neon-border" />
                      <button type="button" onClick={() => setGallery((s) => s.filter((_, j) => j !== i))}
                        className="absolute -top-1 -right-1 rounded-full bg-destructive text-destructive-foreground p-0.5 opacity-0 group-hover:opacity-100 transition">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Payment currency">
              <Select value={String(selectedChainId)} onValueChange={onSelectChain}>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {networks.map((n) => (
                    <SelectItem key={n.id} value={String(n.id)}>
                      {n.nativeCurrency.symbol} — {n.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isConnected && chainId !== selectedChainId && (
                <p className="text-[11px] text-muted-foreground mt-1">Your wallet will be asked to switch to {selectedNetwork?.name}.</p>
              )}
            </Field>
            <Field label={`Mint price in ${currencySymbol} (0 = free)`} error={fieldErrors.mint_price}>
              <Input type="number" step="0.0001" min="0" value={form.mint_price} onChange={(e) => set("mint_price", e.target.value)} aria-invalid={!!fieldErrors.mint_price} />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.unlimited} onCheckedChange={(v) => set("unlimited", v)} id="unl" />
            <Label htmlFor="unl">Unlimited supply</Label>
          </div>
          {!form.unlimited && (
            <Field label="Max supply" error={fieldErrors.max_supply}>
              <Input type="number" min="1" value={form.max_supply} onChange={(e) => set("max_supply", e.target.value)} aria-invalid={!!fieldErrors.max_supply} />
            </Field>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Mint start (optional)" error={fieldErrors.start_at}>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => set("start_at", e.target.value)} />
            </Field>
            <Field label="Mint end (optional)" error={fieldErrors.end_at}>
              <Input type="datetime-local" value={form.end_at} onChange={(e) => set("end_at", e.target.value)} aria-invalid={!!fieldErrors.end_at} />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground rounded border border-primary/30 bg-primary/5 p-2">
            Network: <span className="text-foreground font-semibold">{selectedNetwork.name}</span> · Native token: <span className="text-foreground font-semibold">{selectedNetwork.nativeCurrency.symbol}</span>
            <br />
            Create Drop fee: <span className="text-primary font-semibold">{createFeeEth} {selectedNetwork.nativeCurrency.symbol}</span>
            {Number(createFeeEth) > 0 ? " — sent on-chain to the platform wallet when you confirm." : " — no fee configured for this network."}
          </p>
          <div className="flex justify-end items-center gap-3 pt-2">
            {isConnected ? (
              <Button type="submit" disabled={busy} className="neon-glow">
                {busy ? "Creating…" : "Create Drop"}
              </Button>
            ) : (
              <ConnectButton />
            )}
          </div>
          {!isConnected && (
            <p className="text-xs text-muted-foreground">Connect your wallet to submit. Contracts will be deployed to {chainId ? `chain ${chainId}` : "the selected chain"} in the next step.</p>
          )}
        </form>
      </Card>
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
